import {Injectable, NgZone, OnDestroy, computed, inject, signal} from '@angular/core';

type TabMessage =
  | { type: 'WHO_IS_PRIMARY'; tabId: string }
  | { type: 'PRIMARY_PRESENT'; tabId: string }
  | { type: 'PRIMARY_HEARTBEAT'; tabId: string; timestamp: number }
  | { type: 'PRIMARY_CLOSING'; tabId: string };

@Injectable({providedIn: 'root'})
export class TabManagerService implements OnDestroy {
  private static readonly CHANNEL_NAME = 'corepos-single-tab';
  private static readonly LOCK_NAME = 'corepos-primary-tab';
  private static readonly RELOAD_MARKER_KEY = 'corepos-primary-tab-reloading';
  private static readonly HEARTBEAT_INTERVAL_MS = 2_500;
  private static readonly HEARTBEAT_TIMEOUT_MS = 10_000;
  private static readonly REFRESH_GRACE_MS = 4_000;
  private static readonly MONITOR_INTERVAL_MS = 1_500;
  private static readonly INITIAL_ELECTION_WINDOW_MS = 1_200;
  private static readonly RELOAD_ELECTION_WINDOW_MS = 8_000;
  private static readonly RELOAD_MARKER_VALID_MS = 15_000;
  private static readonly RELOAD_UI_GRACE_MS = 15_000;
  private static readonly INITIAL_RETRY_MS = 150;

  private readonly zone = inject(NgZone);

  readonly currentTabId = this.createTabId();
  readonly primaryTabId = signal<string | null>(null);
  readonly isPrimaryTab = signal(false);
  readonly isBlocked = signal(false);
  readonly isReady = signal(false);
  readonly isSupported = signal(true);
  readonly isReloadRecovery = signal(false);
  readonly isResolving = computed(() => !this.isReady());

  private channel: BroadcastChannel | null = null;
  private initialized = false;
  private destroyed = false;
  private acquisitionInProgress = false;
  private releaseCurrentLock: (() => void) | null = null;
  private heartbeatTimer: number | null = null;
  private monitorTimer: number | null = null;
  private electionTimer: number | null = null;
  private reloadRecoveryTimer: number | null = null;
  private electionDeadline = 0;
  private lastHeartbeatAt = 0;
  private nextTakeoverAt = 0;
  private releasedForPageHide = false;

  private readonly handleBeforeUnload = () => this.releaseForPageExit();
  private readonly handlePageHide = () => this.releaseForPageExit();
  private readonly handlePageShow = (event: PageTransitionEvent) => {
    if (!event.persisted || !this.releasedForPageHide || this.destroyed) {
      return;
    }

    this.releasedForPageHide = false;
    this.isReady.set(false);
    this.isBlocked.set(false);
    this.primaryTabId.set(null);
    void this.tryAcquirePrimaryLock();
  };

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    const hasReloadMarker = this.consumeReloadMarker();
    const isReloadingPrimary = this.isBrowserReload() || hasReloadMarker;
    this.startReloadRecovery(isReloadingPrimary);
    const electionWindow = isReloadingPrimary
      ? TabManagerService.RELOAD_ELECTION_WINDOW_MS
      : TabManagerService.INITIAL_ELECTION_WINDOW_MS;
    this.electionDeadline = Date.now() + electionWindow;

    if (!('locks' in navigator) || !navigator.locks) {
      this.isSupported.set(false);
      this.isBlocked.set(true);
      this.isReady.set(true);
      return;
    }

    this.openBroadcastChannel();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);

    this.monitorTimer = window.setInterval(
      () => this.monitorPrimaryTab(),
      TabManagerService.MONITOR_INTERVAL_MS
    );

    this.postMessage({
      type: 'WHO_IS_PRIMARY',
      tabId: this.currentTabId
    });

    void this.tryAcquirePrimaryLock();
  }

  closeCurrentTab(): void {
    window.close();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.releaseForPageExit();
    this.clearTimers();

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);

    this.channel?.close();
    this.channel = null;
  }

  private async tryAcquirePrimaryLock(): Promise<void> {
    if (
      this.destroyed ||
      this.isPrimaryTab() ||
      this.acquisitionInProgress ||
      Date.now() < this.nextTakeoverAt
    ) {
      return;
    }

    this.acquisitionInProgress = true;

    try {
      await navigator.locks.request(
        TabManagerService.LOCK_NAME,
        {mode: 'exclusive', ifAvailable: true},
        async lock => {
          if (!lock) {
            this.zone.run(() => this.markAsBlocked());
            return;
          }

          this.zone.run(() => this.becomePrimary());

          await new Promise<void>(resolve => {
            this.releaseCurrentLock = resolve;
          });

          this.releaseCurrentLock = null;
          this.stopHeartbeat();
        }
      );
    } catch (error) {
      console.error('No fue posible adquirir el lock de pestaña de CorePOS.', error);
      this.zone.run(() => {
        this.isSupported.set(false);
        this.isPrimaryTab.set(false);
        this.isBlocked.set(true);
        this.isReady.set(true);
      });
    } finally {
      this.acquisitionInProgress = false;
    }
  }

  private becomePrimary(): void {
    this.clearElectionTimer();
    this.finishReloadRecovery();
    this.lastHeartbeatAt = Date.now();
    this.nextTakeoverAt = 0;
    this.primaryTabId.set(this.currentTabId);
    this.isPrimaryTab.set(true);
    this.isBlocked.set(false);
    this.isReady.set(true);

    this.postMessage({
      type: 'PRIMARY_PRESENT',
      tabId: this.currentTabId
    });
    this.sendHeartbeat();
    this.startHeartbeat();
  }

  private markAsBlocked(): void {
    this.isPrimaryTab.set(false);
    this.isBlocked.set(true);

    const now = Date.now();
    const primaryWasConfirmed =
      this.primaryTabId() !== null &&
      this.lastHeartbeatAt >= this.electionDeadline;

    if (now < this.electionDeadline || !primaryWasConfirmed) {
      this.isReady.set(false);
      this.scheduleInitialRetry();
    } else {
      this.isReady.set(true);

      if (!this.lastHeartbeatAt) {
        this.lastHeartbeatAt = Date.now();
      }
    }

    this.postMessage({
      type: 'WHO_IS_PRIMARY',
      tabId: this.currentTabId
    });
  }

  private openBroadcastChannel(): void {
    if (!('BroadcastChannel' in window)) {
      return;
    }

    this.channel = new BroadcastChannel(TabManagerService.CHANNEL_NAME);
    this.channel.addEventListener('message', event => {
      this.zone.run(() => this.handleMessage(event.data as TabMessage));
    });
  }

  private handleMessage(message: TabMessage): void {
    if (!message || message.tabId === this.currentTabId) {
      return;
    }

    switch (message.type) {
      case 'WHO_IS_PRIMARY':
        if (this.isPrimaryTab()) {
          this.postMessage({
            type: 'PRIMARY_PRESENT',
            tabId: this.currentTabId
          });
          this.sendHeartbeat();
        }
        break;

      case 'PRIMARY_PRESENT':
      case 'PRIMARY_HEARTBEAT':
        if (!this.isPrimaryTab()) {
          this.primaryTabId.set(message.tabId);
          this.lastHeartbeatAt =
            message.type === 'PRIMARY_HEARTBEAT'
              ? message.timestamp
              : Date.now();
          this.nextTakeoverAt = 0;
          this.isBlocked.set(true);
          this.revealBlockedAfterInitialElection();
        }
        break;

      case 'PRIMARY_CLOSING':
        if (!this.isPrimaryTab() && this.primaryTabId() === message.tabId) {
          this.primaryTabId.set(null);
          this.nextTakeoverAt = this.isReady()
            ? Date.now() + TabManagerService.REFRESH_GRACE_MS
            : 0;
          this.lastHeartbeatAt = 0;
          this.scheduleInitialRetry();
        }
        break;

    }
  }

  private monitorPrimaryTab(): void {
    if (this.destroyed || this.isPrimaryTab() || !this.isBlocked()) {
      return;
    }

    const now = Date.now();
    const heartbeatExpired =
      !this.lastHeartbeatAt ||
      now - this.lastHeartbeatAt >= TabManagerService.HEARTBEAT_TIMEOUT_MS;
    const graceExpired = !this.nextTakeoverAt || now >= this.nextTakeoverAt;

    if (!heartbeatExpired || !graceExpired) {
      return;
    }

    // Evita intentar agresivamente si el lock sigue ocupado, por ejemplo
    // cuando los timers de una pestaña en segundo plano fueron ralentizados.
    this.lastHeartbeatAt = now;
    this.primaryTabId.set(null);
    void this.tryAcquirePrimaryLock();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(
      () => this.sendHeartbeat(),
      TabManagerService.HEARTBEAT_INTERVAL_MS
    );
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private sendHeartbeat(): void {
    if (!this.isPrimaryTab()) {
      return;
    }

    this.postMessage({
      type: 'PRIMARY_HEARTBEAT',
      tabId: this.currentTabId,
      timestamp: Date.now()
    });
  }

  private releaseForPageExit(): void {
    if (!this.isPrimaryTab() || !this.releaseCurrentLock) {
      return;
    }

    try {
      sessionStorage.setItem(
        TabManagerService.RELOAD_MARKER_KEY,
        String(Date.now())
      );
    } catch {
      // La elección sigue siendo segura con Web Locks aunque el storage no esté disponible.
    }

    this.releasedForPageHide = true;
    this.postMessage({
      type: 'PRIMARY_CLOSING',
      tabId: this.currentTabId
    });
    this.stopHeartbeat();
    this.isPrimaryTab.set(false);
    this.releaseCurrentLock();
  }

  private postMessage(message: TabMessage): void {
    this.channel?.postMessage(message);
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    this.clearElectionTimer();
    this.clearReloadRecoveryTimer();

    if (this.monitorTimer !== null) {
      window.clearInterval(this.monitorTimer);
      this.monitorTimer = null;
    }
  }

  private scheduleInitialRetry(): void {
    if (this.electionTimer !== null || this.destroyed || this.isPrimaryTab()) {
      return;
    }

    this.electionTimer = window.setTimeout(() => {
      this.electionTimer = null;

      const now = Date.now();
      const primaryWasConfirmed =
        this.primaryTabId() !== null &&
        this.lastHeartbeatAt >= this.electionDeadline;

      if (now >= this.electionDeadline && primaryWasConfirmed) {
        this.isReady.set(true);
        return;
      }

      void this.tryAcquirePrimaryLock();
    }, TabManagerService.INITIAL_RETRY_MS);
  }

  private revealBlockedAfterInitialElection(): void {
    if (Date.now() >= this.electionDeadline) {
      this.isReady.set(true);
      return;
    }

    this.isReady.set(false);
    this.scheduleInitialRetry();
  }

  private clearElectionTimer(): void {
    if (this.electionTimer !== null) {
      window.clearTimeout(this.electionTimer);
      this.electionTimer = null;
    }
  }

  private startReloadRecovery(isReloading: boolean): void {
    if (!isReloading) {
      return;
    }

    this.isReloadRecovery.set(true);
    this.reloadRecoveryTimer = window.setTimeout(
      () => this.finishReloadRecovery(),
      TabManagerService.RELOAD_UI_GRACE_MS
    );
  }

  private finishReloadRecovery(): void {
    this.isReloadRecovery.set(false);
    this.clearReloadRecoveryTimer();
  }

  private clearReloadRecoveryTimer(): void {
    if (this.reloadRecoveryTimer !== null) {
      window.clearTimeout(this.reloadRecoveryTimer);
      this.reloadRecoveryTimer = null;
    }
  }

  private createTabId(): string {
    return globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private consumeReloadMarker(): boolean {
    try {
      const value = sessionStorage.getItem(TabManagerService.RELOAD_MARKER_KEY);
      sessionStorage.removeItem(TabManagerService.RELOAD_MARKER_KEY);

      if (!value) {
        return false;
      }

      const timestamp = Number(value);
      return Number.isFinite(timestamp) &&
        Date.now() - timestamp <= TabManagerService.RELOAD_MARKER_VALID_MS;
    } catch {
      return false;
    }
  }

  private isBrowserReload(): boolean {
    const navigation = performance.getEntriesByType('navigation')[0] as
      PerformanceNavigationTiming | undefined;

    if (navigation) {
      return navigation.type === 'reload';
    }

    // Compatibilidad con navegadores antiguos que todavía no exponen
    // PerformanceNavigationTiming.
    return performance.navigation?.type === 1;
  }
}
